/*
 * ONDO cart engine v2 (ES module, singleton) — a thin layer over Liquid Ajax Cart v2 (LAC, assets/m-cart.js).
 * Shared by the product card, the quick-add / product form, the sticky bar, the configurator and <ondo-cart>.
 *
 *   import { addToCart } from '{{ "ondo-cart.js" | asset_url }}';   // always the versioned asset_url, never './ondo-cart.js'
 *   const res = await addToCart([{ id: 123, quantity: 1 }], { source: element });
 *
 * API (all async calls resolve with a Result, they never throw):
 *   addToCart(itemsOrFormData, { source, open = true })   → LAC add (items: { id, quantity, properties, parent_id, selling_plan })
 *   changeLine(key, quantity, { source })                 → LAC change { id: key, quantity }
 *   removeLines(keys, { source })                         → LAC update { updates: { key: 0, … } } (one request)
 *   updateCart(body, { source, action = 'update' })       → LAC update (note, discount, attributes, …)
 *   applyDiscount(code, { existing = [], source })        → { …Result, reason: '' | 'invalid' | 'error', applied }
 *   removeDiscount(code, { existing = [], source })
 *   refresh({ source })                                   → LAC update {} (re-renders the cart sections)
 *   fetchCart()                                           → cart JSON (LAC state or GET cart.js)
 *   open(trigger) / close()                               → dispatch ondo:cart:open / ondo:cart:close (handled by the drawer)
 *   isBusy()                                              → true while the LAC queue (or a fallback request) runs
 *   Result = { ok, status, message, cart | null, items | null, sections | null, requestState | null }
 *   Global: window.OndoCart = { __v: 2, add, change, remove, update, applyDiscount, removeDiscount, refresh, fetchCart, open, close, isBusy }
 *
 * LAC stays the single queue for every writer (ours, the configurator's liquidAjaxCart.add(FormData), legacy forms).
 * Each cart section renders one empty hook [data-ajax-cart-section], so LAC appends the cart section ids to every request.
 * This module listens to liquid-ajax-cart:request-end (any writer) and turns the response into document events:
 *   ondo:cart:updated { action, requestType, sections, cart, ok, source, keys, lost } (<ondo-cart> swaps its regions)
 *   ondo:cart:added   { items, cart, message, status, source }                    (only for addToCart's own successful adds)
 *   ondo:cart:error   { action, requestType, message, status, keys, items, source } (action 'external' = another writer)
 *   ondo:cart:busy    { busy }
 *   ondo:cart:open    { trigger }  on every successful add (unless info.ondoCart.open === false), when a drawer exists
 *   ondo:cart:configuration-lost { lost: [{ cfg, type: 'removed' | 'reduced' }], cart, action, source }
 *     A mutating request (any writer) made a configurator configuration disappear (base gone) or lose charms although the
 *     request did not target any of its lines: Shopify drops / trims the other lines of a variant when one line takes
 *     its whole stock. Before/after = LAC previousCart / cart (else the last cart this engine saw). <ondo-cart> tells the
 *     user (never a silent loss); `lost` is also in ondo:cart:updated.
 * and syncs the header badge [data-ondo-cart-count] from data-count-value of the returned cart HTML (else cart.item_count).
 * LAC info used: info.initiator (source) and info.ondoCart = { action, open, keys, items }.
 * Fallback when LAC is not ready after 4 s: fetch the same endpoint with sections = the [data-ondo-cart-section] sections;
 * without any cart section on the page an add redirects to the cart page (previous behaviour).
 */

const VERSION = 2;
const LAC_TIMEOUT = 4000;
const SECTION_PREFIX = 'shopify-section-';
const ENDPOINTS = { add: 'cart/add.js', change: 'cart/change.js', update: 'cart/update.js' };

function createEngine() {
  const state = { busy: false, fallbackBusy: 0, lastCart: null, ringJobs: 0 };
  let ringQueue = Promise.resolve();

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = () => (window.routes && window.routes.cart_url) || `${routesRoot()}cart`;

  function errorText() {
    const host = document.querySelector('ondo-cart[data-t-error]');
    if (host && host.dataset.tError) return host.dataset.tError;
    return (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
  }

  // Shopify error bodies: { errors: "…" | { field: ["…"] } } or { status, message, description }
  function messageFrom(body) {
    if (!body || typeof body !== 'object') return errorText();
    const raw = body.errors || body.description || body.message;
    if (typeof raw === 'string' && raw.trim()) return raw;
    if (raw && typeof raw === 'object') {
      const text = Object.values(raw).map((v) => (Array.isArray(v) ? v.join(', ') : String(v))).join('; ');
      if (text.trim()) return text;
    }
    return errorText();
  }

  const asObject = (value) => (value && typeof value === 'object' ? value : null);
  const isElement = (value) => typeof Element !== 'undefined' && value instanceof Element;
  const isFormData = (value) => typeof FormData !== 'undefined' && value instanceof FormData;
  const lower = (value) => String(value || '').trim().toLowerCase();

  function uniqCaseInsensitive(list) {
    const seen = new Set();
    const out = [];
    list.forEach((value) => {
      const clean = String(value || '').trim();
      if (!clean || seen.has(clean.toLowerCase())) return;
      seen.add(clean.toLowerCase());
      out.push(clean);
    });
    return out;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function mergeSections(...bodies) {
    const out = {};
    bodies.forEach((body) => {
      const sections = body && asObject(body.sections);
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        if (typeof sections[id] === 'string') out[id] = sections[id];
      });
    });
    return out;
  }

  function lacCart() {
    const lac = window.liquidAjaxCart;
    try {
      return lac && lac.init && lac.cart ? lac.cart : null;
    } catch (err) {
      return null;
    }
  }

  function cartFrom(rs) {
    const res = rs && rs.responseData;
    const extra = rs && rs.extraResponseData;
    const xbody = asObject(extra && extra.body);
    const body = asObject(res && res.body);
    if (extra && extra.ok && xbody && xbody.token) return xbody;
    if (res && res.ok && body && body.token) return body;
    return null;
  }

  // Sections of the cart UI on this page (fallback transport only: with LAC the hook does this)
  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('[data-ondo-cart-section]').forEach((el) => {
      const section = el.closest(`[id^="${SECTION_PREFIX}"]`);
      if (!section) return;
      const id = section.id.slice(SECTION_PREFIX.length);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function syncCount(sections, cart) {
    let value = null;
    Object.keys(sections).some((id) => {
      const match = /data-count-value="(\d+)"/.exec(sections[id]);
      if (match) value = Number(match[1]);
      return !!match;
    });
    if (value === null && cart && typeof cart.item_count === 'number') value = cart.item_count;
    if (value === null) return;
    document.querySelectorAll('[data-ondo-cart-count]').forEach((el) => {
      el.textContent = String(value);
      el.setAttribute('data-count', String(value));
    });
  }

  function openCart(trigger) {
    emit('ondo:cart:open', { trigger: isElement(trigger) ? trigger : null });
  }

  function closeCart() {
    emit('ondo:cart:close', {});
  }

  function isQuietRefresh(rs) {
    if (rs.requestType !== 'update') return false;
    const body = rs.requestBody;
    if (!body) return true;
    if (isFormData(body)) return Array.from(body.keys()).every((k) => k === 'sections');
    return Object.keys(body).every((k) => k === 'sections');
  }

  /* ---------- configuration integrity (configurator cart contract v1: `_ondo_cfg` + `_ondo_role` base / charm) ---------- */

  function configurations(cart) {
    const map = new Map(); // cfg → { base, charms (units), keys }
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach((item) => {
      const props = (item && item.properties) || {};
      const cfg = props._ondo_cfg ? String(props._ondo_cfg) : '';
      const role = props._ondo_role;
      // change.js answers with the lines Shopify just dropped still listed, at quantity 0: they are gone
      if (!cfg || (role !== 'base' && role !== 'charm') || !(Number(item.quantity) > 0)) return;
      const entry = map.get(cfg) || { base: false, charms: 0, keys: [] };
      if (role === 'base') entry.base = true;
      else entry.charms += Number(item.quantity) || 0;
      entry.keys.push(String(item.key));
      map.set(cfg, entry);
    });
    return map;
  }

  // Line keys the request itself changes (line key, variant id → its lines, 1-based line index)
  function targetedKeys(rs, before) {
    const out = new Set();
    const items = before && Array.isArray(before.items) ? before.items : [];
    const ref = (value) => {
      const v = String(value === undefined || value === null ? '' : value).trim();
      if (!v) return;
      if (v.indexOf(':') !== -1) { out.add(v); return; }
      items.forEach((it) => { if (String(it.variant_id) === v || String(it.id) === v) out.add(String(it.key)); });
    };
    const line = (value) => {
      const it = items[(parseInt(value, 10) || 0) - 1];
      if (it) out.add(String(it.key));
    };
    const body = rs.requestBody;
    if (!body) return out;
    const isParams = isFormData(body) || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams);
    if (isParams) {
      let index = 0;
      Array.from(body.entries()).forEach(([name, value]) => {
        const match = /^updates\[(.+)\]$/.exec(name);
        if (match) ref(match[1]);
        else if (name === 'updates[]') { line(index + 1); index += 1; }
        else if (rs.requestType === 'change' && name === 'id') ref(value);
        else if (rs.requestType === 'change' && name === 'line') line(value);
      });
      return out;
    }
    if (rs.requestType === 'change') {
      if (body.id !== undefined) ref(body.id);
      if (body.line !== undefined) line(body.line);
    }
    if (body.updates && typeof body.updates === 'object') {
      if (Array.isArray(body.updates)) body.updates.forEach((qty, i) => line(i + 1));
      else Object.keys(body.updates).forEach(ref);
    }
    return out;
  }

  function lostConfigurations(rs, before, after) {
    if (!before || !after || rs.requestType === 'clear' || rs.requestType === 'get' || isQuietRefresh(rs)) return [];
    const previous = configurations(before);
    if (!previous.size) return [];
    const next = configurations(after);
    const targeted = targetedKeys(rs, before);
    const lost = [];
    previous.forEach((entry, cfg) => {
      if (!entry.base || entry.keys.some((key) => targeted.has(key))) return; // not a configuration / changed on purpose
      const now = next.get(cfg);
      if (!now || !now.base) lost.push({ cfg, type: 'removed' });
      else if (now.charms < entry.charms) lost.push({ cfg, type: 'reduced' });
    });
    return lost;
  }

  // One handler for every finished request: LAC (any writer) and our own fallback fetches
  function onRequestEnd(rs, detailCart, detailPrevious) {
    if (!rs) return;
    const info = rs.info || {};
    if (info.cancel) return;
    const meta = asObject(info.ondoCart);
    const res = rs.responseData;
    const ok = !!(res && res.ok);
    const body = asObject(res && res.body);
    const xbody = asObject(rs.extraResponseData && rs.extraResponseData.body);
    const sections = mergeSections(body, xbody);
    const cart = asObject(detailCart) || cartFrom(rs);
    const action = (meta && meta.action) || 'external';
    const source = info.initiator === undefined ? null : info.initiator;
    const keys = (meta && meta.keys) || null;
    let lost = [];
    if (cart) {
      try {
        lost = lostConfigurations(rs, asObject(detailPrevious) || state.lastCart, cart);
      } catch (err) {
        lost = [];
      }
      state.lastCart = cart;
    }

    syncCount(sections, cart);
    if (Object.keys(sections).length || cart) {
      emit('ondo:cart:updated', { action, requestType: rs.requestType, sections, cart, ok, source, keys, lost });
    }
    if (lost.length) emit('ondo:cart:configuration-lost', { lost, cart, action, source });
    if (rs.requestType === 'add' && ok && !(meta && meta.open === false) && document.querySelector('ondo-cart-drawer')) {
      openCart(source);
    }
    if (!ok && !(action === 'external' && isQuietRefresh(rs))) {
      emit('ondo:cart:error', {
        action,
        requestType: rs.requestType,
        message: rs.fetchError || !res ? errorText() : messageFrom(body),
        status: res ? res.status : 0,
        keys,
        items: (meta && meta.items) || null,
        source,
      });
    }
  }

  function resultFrom(rs, type) {
    const res = rs && rs.responseData;
    const ok = !!(res && res.ok);
    const body = asObject(res && res.body);
    const xbody = asObject(rs && rs.extraResponseData && rs.extraResponseData.body);
    let cart = cartFrom(rs);
    if (!cart && ok) cart = lacCart();
    let items = null;
    if (type === 'add' && ok && body) {
      if (Array.isArray(body.items)) items = body.items;
      else if (body.id) items = [body];
    }
    const sections = mergeSections(body, xbody);
    return {
      ok,
      status: res ? res.status : 0,
      message: ok ? '' : (rs && rs.fetchError) || !res ? errorText() : messageFrom(body),
      cart,
      items,
      sections: Object.keys(sections).length ? sections : null,
      requestState: rs || null,
    };
  }

  const failure = (message) => ({
    ok: false, status: 0, message: message || errorText(), cart: null, items: null, sections: null, requestState: null,
  });

  function lacReady() {
    const lac = window.liquidAjaxCart;
    if (lac && lac.init) return Promise.resolve(true);
    // LAC is loaded by the cart component (and the legacy <coretex-cart>): no hook and no LAC → fallback right away
    if (!lac && !document.querySelector('[data-ajax-cart-section], coretex-cart')) return Promise.resolve(false);
    return new Promise((resolve) => {
      let timer = null;
      const done = (value) => {
        clearTimeout(timer);
        document.removeEventListener('liquid-ajax-cart:init', onInit);
        resolve(value);
      };
      const onInit = () => done(true);
      timer = setTimeout(() => done(!!(window.liquidAjaxCart && window.liquidAjaxCart.init)), LAC_TIMEOUT);
      document.addEventListener('liquid-ajax-cart:init', onInit);
    });
  }

  function viaLac(type, body, meta, source) {
    return new Promise((resolve) => {
      try {
        window.liquidAjaxCart[type](body, {
          info: { initiator: source, ondoCart: meta },
          lastCallback: (rs) => resolve(resultFrom(rs, type)),
        });
      } catch (err) {
        resolve(failure());
      }
    });
  }

  function setFallbackBusy(delta) {
    const before = state.fallbackBusy > 0;
    state.fallbackBusy = Math.max(0, state.fallbackBusy + delta);
    const after = state.fallbackBusy > 0;
    if (before !== after && !state.busy) emit('ondo:cart:busy', { busy: after });
  }

  async function postJson(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, body };
  }

  async function viaFetch(type, body, meta, source) {
    const ids = cartSectionIds();
    const url = `${routesRoot()}${ENDPOINTS[type]}`;
    const rs = { requestType: type, endpoint: url, requestBody: body, info: { initiator: source, ondoCart: meta } };
    setFallbackBusy(1);
    try {
      let response;
      if (isFormData(body)) {
        if (ids.length) {
          body.set('sections', ids.join(','));
          body.set('sections_url', window.location.pathname);
        }
        response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'x-requested-with': 'XMLHttpRequest', Accept: 'application/json' },
          body,
        });
        const json = await response.json().catch(() => null);
        rs.responseData = { ok: response.ok, status: response.status, body: json };
      } else {
        const payload = { ...(body || {}) };
        if (ids.length) {
          payload.sections = ids.join(',');
          payload.sections_url = window.location.pathname;
        }
        rs.responseData = await postJson(url, payload);
      }
      // As LAC does: after an add (no cart state in the response) or a failed request (e.g. a partial add on 422),
      // ask for the real cart + sections
      if (ids.length && (type === 'add' || !rs.responseData.ok)) {
        rs.extraResponseData = await postJson(`${routesRoot()}${ENDPOINTS.update}`, {
          updates: {},
          sections: ids.join(','),
          sections_url: window.location.pathname,
        }).catch(() => null);
      }
    } catch (err) {
      rs.responseData = null;
      rs.fetchError = err;
    } finally {
      setFallbackBusy(-1);
    }

    const result = resultFrom(rs, type);
    if (!ids.length) {
      // No cart UI on this page: keep the previous behaviour (show the cart page after an add)
      if (type === 'add' && result.ok) window.setTimeout(() => window.location.assign(cartUrl()), 0);
      if (!result.ok) onRequestEnd(rs, null);
      return result;
    }
    onRequestEnd(rs, null);
    return result;
  }

  function ringGiftConfig() {
    try { return JSON.parse(document.querySelector('[data-ondo-ring-gift-config]')?.textContent || '{}'); }
    catch (_) { return {}; }
  }

  function ringGiftUpdates(cart, cfg) {
    const items = cart?.items || [];
    const eligible = new Set((cfg.eligible || []).map(Number));
    const qualifies = cfg.enabled && items.some(i => i.quantity > 0 && eligible.has(Number(i.product_id)) && i.properties?._ondo_ring_gift !== 'v1');
    let kept = false;
    let nonFree = false;
    const updates = {};
    for (const item of items) {
      if (item.properties?._ondo_ring_gift !== 'v1' || !(item.quantity > 0)) continue;
      // Private marker alone never grants a discount; Shopify's automatic discount is authoritative.
      const correctProduct = Number(item.product_id) === Number(cfg.giftId);
      let quantity = 0;
      if (qualifies && correctProduct && !kept) {
        quantity = 1;
        kept = true;
        if (item.quantity === 1 && Number(item.final_line_price) !== 0) {
          quantity = 0;
          nonFree = true;
        }
      }
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    return { updates, nonFree };
  }

  async function sendRaw(type, body, meta, source) {
    const lac = await lacReady();
    return lac ? viaLac(type, body, meta, source) : viaFetch(type, body, meta, source);
  }

  async function sendRingSafe(type, body, meta, source) {
    const cfg = ringGiftConfig();
    const configured = Array.isArray(cfg.eligible) && cfg.giftId;
    const result = await sendRaw(type, body, configured ? { ...meta, open: false } : meta, source);
    if (!configured) return result;
    let cart = result.cart;
    if (!cart) {
      try {
        const response = await fetch(`${routesRoot()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (response.ok) cart = await response.json();
      } catch (_) {}
    }
    // Two bounded passes: first cap quantity to one, then check its recalculated discount.
    for (let pass = 0; cart && pass < 2; pass++) {
      const correction = ringGiftUpdates(cart, cfg);
      if (!Object.keys(correction.updates).length) break;
      const fixed = await sendRaw('update', { updates: correction.updates }, { action: 'ring-gift-sync', open: false, keys: Object.keys(correction.updates) }, source);
      if (!fixed.ok) { result.warning = cfg.error; break; }
      cart = fixed.cart;
      result.cart = cart;
      result.sections = fixed.sections;
      if (correction.nonFree) result.warning = cfg.discountError;
    }
    if (type === 'add' && result.ok && meta.open !== false && document.querySelector('ondo-cart-drawer')) openCart(source);
    if (result.warning) emit('ondo:ring-gift:warning', { message: result.warning });
    return result;
  }

  function send(type, body, meta, source) {
    state.ringJobs += 1;
    const task = ringQueue.then(() => sendRingSafe(type, body, meta, source));
    ringQueue = task.catch(() => {});
    return task.catch(() => failure()).finally(() => { state.ringJobs -= 1; });
  }

  function normaliseItems(input) {
    let list;
    if (Array.isArray(input)) list = input;
    else if (input && Array.isArray(input.items)) list = input.items;
    else list = [input];
    return list
      .filter((item) => item && item.id)
      .map((item) => {
        const out = { ...item, id: Number(item.id), quantity: Math.max(1, parseInt(item.quantity, 10) || 1) };
        if (out.parent_id !== undefined && out.parent_id !== null && out.parent_id !== '') out.parent_id = Number(out.parent_id);
        else delete out.parent_id;
        return out;
      });
  }

  async function add(itemsOrFormData, { source = null, open = true } = {}) {
    let payload;
    let list = null;
    if (isFormData(itemsOrFormData)) {
      payload = itemsOrFormData;
    } else {
      list = normaliseItems(itemsOrFormData);
      if (!list.length) {
        const result = failure();
        emit('ondo:cart:error', {
          action: 'add', requestType: 'add', message: result.message, status: 0, keys: null, items: list, source,
        });
        return result;
      }
      payload = { items: list };
    }
    const result = await send('add', payload, { action: 'add', open: open !== false, keys: null, items: list }, source);
    if (result.ok) {
      emit('ondo:cart:added', {
        items: list || result.items || [], cart: result.cart, message: '', status: result.status, source,
      });
    }
    return result;
  }

  function change(key, quantity, { source = null } = {}) {
    const qty = Math.max(0, parseInt(quantity, 10) || 0);
    return send('change', { id: String(key), quantity: qty }, { action: 'change', open: false, keys: [String(key)] }, source);
  }

  function remove(keys, { source = null } = {}) {
    const list = (Array.isArray(keys) ? keys : String(keys || '').split(','))
      .map((k) => String(k).trim())
      .filter(Boolean);
    if (!list.length) return Promise.resolve(failure());
    const updates = {};
    list.forEach((k) => { updates[k] = 0; });
    return send('update', { updates }, { action: 'remove', open: false, keys: list }, source);
  }

  function update(body, { source = null, action = 'update' } = {}) {
    return send('update', body || {}, { action, open: false, keys: null }, source);
  }

  async function applyDiscount(code, { existing = [], source = null } = {}) {
    const clean = String(code || '').trim();
    if (!clean) return { ...failure(), reason: 'error', applied: false };
    const previous = uniqCaseInsensitive(existing || []);
    const codes = uniqCaseInsensitive([...previous, clean]);
    const res = await update({ discount: codes.join(',') }, { source, action: 'discount' });
    if (!res.ok) return { ...res, reason: 'error', applied: false };
    const list = res.cart && Array.isArray(res.cart.discount_codes) ? res.cart.discount_codes : [];
    const hit = list.find((d) => d && lower(d.code) === lower(clean));
    if (hit && hit.applicable === false) {
      // Shopify keeps a non-applicable code in the cart: put the previous codes back right away
      await update({ discount: previous.join(',') }, { source, action: 'discount' });
      return { ...res, ok: false, reason: 'invalid', message: '', applied: false };
    }
    return { ...res, ok: true, reason: '', applied: !!hit };
  }

  function removeDiscount(code, { existing = [], source = null } = {}) {
    const rest = uniqCaseInsensitive(existing || []).filter((c) => lower(c) !== lower(code));
    return update({ discount: rest.join(',') }, { source, action: 'discount' });
  }

  function refresh({ source = null } = {}) {
    return update({}, { source, action: 'refresh' });
  }

  async function fetchCart() {
    const cached = lacCart();
    if (cached) return cached;
    try {
      const response = await fetch(`${routesRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (err) {
      return null;
    }
  }

  function isBusy() {
    let lacBusy = false;
    try {
      lacBusy = !!(window.liquidAjaxCart && window.liquidAjaxCart.init && window.liquidAjaxCart.processing);
    } catch (err) {
      lacBusy = false;
    }
    return state.ringJobs > 0 || state.busy || lacBusy || state.fallbackBusy > 0;
  }

  // Global listeners (installed once per page, whatever the asset URL version)
  document.addEventListener('liquid-ajax-cart:request-end', (event) => {
    const detail = event.detail || {};
    try {
      onRequestEnd(detail.requestState, detail.cart || null, detail.previousCart || null);
    } catch (err) {
      console.error('ondo-cart: request-end handler failed', err); // eslint-disable-line no-console
    }
  });
  // Baseline for the fallback transport (with LAC every request-end carries previousCart)
  state.lastCart = lacCart();
  document.addEventListener('liquid-ajax-cart:init', () => { if (!state.lastCart) state.lastCart = lacCart(); });
  document.addEventListener('liquid-ajax-cart:queue-start', () => {
    state.busy = true;
    emit('ondo:cart:busy', { busy: true });
  });
  document.addEventListener('liquid-ajax-cart:queue-end', () => {
    state.busy = false;
    emit('ondo:cart:busy', { busy: state.fallbackBusy > 0 });
  });

  return {
    __v: VERSION,
    add,
    change,
    remove,
    update,
    applyDiscount,
    removeDiscount,
    refresh,
    fetchCart,
    open: openCart,
    close: closeCart,
    isBusy,
    lacReady,
  };
}

const engine = window.OndoCart && window.OndoCart.__v === VERSION ? window.OndoCart : createEngine();
window.OndoCart = engine;

export const addToCart = (itemsOrFormData, options) => engine.add(itemsOrFormData, options);
export const changeLine = (key, quantity, options) => engine.change(key, quantity, options);
export const removeLines = (keys, options) => engine.remove(keys, options);
export const updateCart = (body, options) => engine.update(body, options);
export const applyDiscount = (code, options) => engine.applyDiscount(code, options);
export const removeDiscount = (code, options) => engine.removeDiscount(code, options);
export const refresh = (options) => engine.refresh(options);
export const fetchCart = () => engine.fetchCart();
export const open = (trigger) => engine.open(trigger);
export const close = () => engine.close();
export const isBusy = () => engine.isBusy();
export default engine;
